// THE CALCULATOR. Pure, and shared: the app runs it on every keystroke to show
// the number before it is saved, and the api runs it again on save to store
// the number it shows. One module, so the two can never disagree.
//
// Mifflin–St Jeor for the energy burnt at rest, times an activity multiplier
// for the day's maintenance, plus or minus 500 kcal for the goal — 500 a day
// is roughly 0.5 kg a week. Rounded to the nearest 10, and never under
// FLOOR, the figure below which a diet is usually only advised under medical
// supervision.
import { TARGET_MAX } from "./fields";

// kcal = 10 × kg + 6.25 × cm − 5 × years + s, where s is the formula's
// constant. "other" is the midpoint of the two.
export const GENDER_CONSTANT = { male: 5, female: -161, other: -78 };

export const ACTIVITY_FACTOR = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very: 1.9,
};

export const GOAL_ADJUSTMENT = { lose: -500, maintain: 0, gain: 500 };

export const FLOOR = 1200;

const round10 = (value) => Math.round(value / 10) * 10;

// The calculator's whole working, or null while any input is missing — the
// screen shows nothing rather than a number from half a profile.
export const calculate = ({ age, gender, weight, height, activity, goal } = {}) => {
  const a = Number(age);
  const w = Number(weight);
  const h = Number(height);
  const s = GENDER_CONSTANT[gender];
  const factor = ACTIVITY_FACTOR[activity];
  const adjustment = GOAL_ADJUSTMENT[goal];

  if (!(a > 0 && w > 0 && h > 0) || s === undefined || factor === undefined || adjustment === undefined) {
    return null;
  }

  const bmr = 10 * w + 6.25 * h - 5 * a + s;
  const maintenance = bmr * factor;
  const wanted = round10(maintenance + adjustment);
  const calculated = Math.min(TARGET_MAX, Math.max(FLOOR, wanted));

  return {
    bmr: Math.round(bmr),
    maintenance: round10(maintenance),
    adjustment,
    calculated,
    // True when the floor, not the formula, set the number — the screen says
    // so rather than letting 1,200 look like what the formula worked out.
    floored: wanted < FLOOR,
  };
};

// The target a profile sets: the person's own number when they gave one, the
// calculated one otherwise. Null when there is neither.
export const targetFor = (profile = {}) => {
  const custom = profile.customTarget;
  if (custom !== null && custom !== undefined && custom !== "" && Number(custom) > 0) return Number(custom);
  const result = calculate(profile);
  return result ? result.calculated : null;
};

// A split of a calorie target into macro grams, for the screen to offer as a
// start: protein at 1.6 g per kg of body weight (the usual figure for someone
// keeping muscle while they change weight), fat at 30% of the energy, and
// carbs the rest. Null without a target. Never stored as it is — the person
// keeps, changes or clears it, and what they save is theirs.
export const PROTEIN_PER_KG = 1.6;
export const FAT_SHARE = 0.3;

export const macroSuggestion = ({ target, weight } = {}) => {
  const kcal = Number(target);
  if (!(kcal > 0)) return null;
  const kg = Number(weight);
  const protein = Math.round(kg > 0 ? kg * PROTEIN_PER_KG : (kcal * 0.25) / 4);
  const fat = Math.round((kcal * FAT_SHARE) / 9);
  const carbs = Math.max(0, Math.round((kcal - protein * 4 - fat * 9) / 4));
  return { protein, carbs, fat };
};
